import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Chip from '@mui/material/Chip';
import CheckIcon from '@mui/icons-material/Check';
import PublicIcon from '@mui/icons-material/Public';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import * as api from '../api';
import Header from '../components/Header';
import validator from 'validator';

// ---------------------------------------------------------------------------
// Pricing Section
// ---------------------------------------------------------------------------
function Pricing() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const tiers = [
    {
      title: t('home.pricingTrial'),
      price: t('home.pricingFree'),
      subtitle: t('home.pricingTrialSub'),
      color: '#9e9e9e',
      features: [
        t('home.featureRegistration'),
        t('home.featureStages'),
        t('home.featureSquads'),
        t('home.featureScoring'),
        t('home.featureLiveScores'),
        t('home.featureRo'),
      ],
    },
    {
      title: t('home.pricingUnlimited'),
      price: t('home.pricingUnlimitedPrice'),
      subtitle: t('home.pricingUnlimitedSub'),
      color: '#e57373',
      features: [
        t('home.featureRegistration'),
        t('home.featureStages'),
        t('home.featureSquads'),
        t('home.featureScoring'),
        t('home.featureLiveScores'),
        t('home.featureStats'),
        t('home.featureAnnouncements'),
        t('home.featureWebsite'),
      ],
    },
  ];

  const PricingCard = ({ title, price, subtitle, color, features }) => (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ background: color, py: 2, px: 1 }}>
        <Typography variant="h6" sx={{ color: 'white' }}>{title}</Typography>
        <Typography sx={{ color: 'white' }}>{price}</Typography>
        {subtitle && <Typography variant="caption" sx={{ color: 'white' }}>{subtitle}</Typography>}
      </Box>
      <Box sx={{ p: 2, flexGrow: 1 }}>
        {features.map((f) => (
          <Box key={f} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
            <CheckIcon sx={{ mr: 1 }} />
            <span>{f}</span>
          </Box>
        ))}
      </Box>
      <Box sx={{ p: 1 }}>
        <Button fullWidth variant="contained" color="primary" onClick={() => navigate('/new')}>
          {t('home.signUp')}
        </Button>
      </Box>
    </Paper>
  );

  return (
    <Grid size={12} sx={{ px: 1.5, textAlign: 'center' }}>
      <Typography variant="h4" sx={{ mt: 3 }}>{t('home.pricing')}</Typography>
      <Typography variant="body1">{t('home.pricingSubtitle')}</Typography>
      <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', mt: 2, flexWrap: 'wrap' }}>
        {tiers.map((tier) => (
          <Box key={tier.title} sx={{ width: { xs: '100%', md: '25%' } }}>
            <PricingCard {...tier} />
          </Box>
        ))}
      </Box>

      <Typography variant="h5" sx={{ mt: 3 }}>{t('home.pricingWebsiteOnly')}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Box sx={{ width: { xs: '100%', md: '25%' } }}>
          <PricingCard
            title={t('home.pricingWebsite')}
            price={t('home.pricingWebsitePrice')}
            subtitle=""
            color="#81c784"
            features={[t('home.featureMatchInfo'), t('home.featureRegistration'), t('home.featureStages')]}
          />
        </Box>
      </Box>
    </Grid>
  );
}

// ---------------------------------------------------------------------------
// Contact Us Section
// ---------------------------------------------------------------------------
function ContactUs() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const onSend = async () => {
    const errs = {};
    if (!name) errs.name = true;
    if (!email || !validator.isEmail(email)) errs.email = true;
    if (!message) errs.message = true;
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSending(true);
    setResult(null);
    try {
      await api.submitFeedback({ name, email, message });
      setResult('success');
    } catch {
      setResult('error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Grid size={12} sx={{ px: 1.5, textAlign: 'center' }}>
      <Typography variant="h4" sx={{ mt: 3, mb: 1.5 }}>{t('home.contactUs')}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Paper sx={{ p: 3, width: '100%', maxWidth: 500 }}>
          <TextField label={t('home.contactName')} value={name} onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: false })); }} error={!!errors.name} fullWidth sx={{ mb: 1.5 }} />
          <TextField label={t('home.contactEmail')} value={email} onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: false })); }} error={!!errors.email} fullWidth sx={{ mb: 1.5 }} />
          <TextField label={t('home.contactMessage')} value={message} onChange={(e) => { setMessage(e.target.value); setErrors((p) => ({ ...p, message: false })); }} error={!!errors.message} fullWidth sx={{ mb: 1.5 }} />
          <Button fullWidth variant="contained" color="secondary" onClick={sending ? undefined : onSend}>
            {sending ? <CircularProgress size={18} sx={{ color: 'white' }} /> : t('home.contactSend')}
          </Button>
          {result === 'error' && (
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
              {t('home.contactError')}
            </Typography>
          )}
          {result === 'success' && (
            <Typography variant="caption" sx={{ color: 'success.main', display: 'block', mt: 1 }}>
              {t('home.contactSuccess')}
            </Typography>
          )}
        </Paper>
      </Box>
    </Grid>
  );
}

// ---------------------------------------------------------------------------
// CTA Buttons (Sign Up / Demo / New Match)
// ---------------------------------------------------------------------------
function CtaButtons({ isAuthenticated, onDemo }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <Button variant="contained" color="primary" sx={{ mx: 1.5, height: 40, width: '70%' }} onClick={() => navigate('/new')}>
          {t('home.newMatch')}
        </Button>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
      <Button variant="contained" color="primary" sx={{ mx: 1.5, height: 40, width: '42%' }} onClick={() => navigate('/new')}>
        {t('home.signUp')}
      </Button>
      <Button variant="contained" color="secondary" sx={{ mx: 1.5, height: 40, width: '42%' }} onClick={onDemo}>
        {t('home.demo')}
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Home Page (Aria landing page)
// ---------------------------------------------------------------------------
export default function Home({ locale, onLocaleChange }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [latestMatches, setLatestMatches] = useState(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoMatchId, setDemoMatchId] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const onDemo = async () => {
    setDemoOpen(true);
    setDemoMatchId(null);
    const demoId = Date.now();
    await api.duplicateDemo({ demoId });
    setDemoMatchId(`demo-${demoId}`);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const res = await api.getMatchesPublic();
        const data = res.data;
        const matchList = Object.keys(data).map((k) => ({ ...data[k], alias: data[k].alias || k }));

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);

        const current = matchList.filter((m) => {
          const start = new Date(m.startDate.year, m.startDate.month - 1, m.startDate.day);
          const end = m.endDate ? new Date(m.endDate.year, m.endDate.month - 1, m.endDate.day) : start;
          return start <= todayEnd && end >= today;
        });

        const upcoming = matchList
          .filter((m) => {
            const start = new Date(m.startDate.year, m.startDate.month - 1, m.startDate.day);
            return start > todayEnd;
          })
          .sort((a, b) => {
            const dA = new Date(a.startDate.year, a.startDate.month - 1, a.startDate.day);
            const dB = new Date(b.startDate.year, b.startDate.month - 1, b.startDate.day);
            return dA - dB;
          })
          .slice(0, 5);

        setLatestMatches([...current, ...upcoming]);
      } catch {
        // ignore
      }
    })();
  }, []);

  const imgStyle = { width: '100%' };
  const innerPaper = {
    display: 'flex',
    p: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    maxWidth: '66%',
    margin: 'auto',
  };

  const features = [
    t('home.feature1'), t('home.feature2'), t('home.feature3'), t('home.feature4'),
    t('home.feature5'), t('home.feature6'), t('home.feature7'), t('home.feature8'),
  ];
  const featuresLeft = features.slice(0, 4);
  const featuresRight = features.slice(4);

  return (
    <Grid container sx={{ textAlign: 'center' }}>
      <Header locale={locale} onLocaleChange={onLocaleChange} />

      {/* Hero / Top Section */}
      <Grid size={12} sx={{ pt: '92px', background: 'white', position: 'relative' }}>
        <Typography variant="h2">&quot;Aria&quot;</Typography>
        <Typography variant="h6" sx={{ fontWeight: 200 }}>
          {t('home.subtitle')}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 200 }}>
          {t('home.tagline')}
        </Typography>
        <Box component="img" src="/scoring-hand.png" alt="Aria scoring" sx={imgStyle} />
      </Grid>

      {/* Mobile CTA */}
      {isMobile && (
        <Grid size={12} sx={{ pt: 3 }}>
          <CtaButtons isAuthenticated={isAuthenticated} onDemo={onDemo} />
        </Grid>
      )}

      {/* How does it work? */}
      <Grid size={12}>
        <Typography variant="h4" sx={{ my: 3 }}>{t('home.howDoesItWork')}</Typography>
      </Grid>

      {/* Step 1 - Pick a stage */}
      <Grid size={12}>
        <Paper sx={{ mx: 1.5 }}>
          <Grid container>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box component="img" src="/scoring-stage.png" alt="Pick stage screen" sx={imgStyle} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={innerPaper}>
              <Typography variant={isMobile ? 'h4' : 'h2'}>
                {t('home.step1Title')}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1.5 }}>
                {t('home.step1Desc1')}
              </Typography>
              <Typography variant="body1">
                {t('home.step1Desc2')}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Step 2 - Pick a squad + Attendance */}
      <Grid size={12}>
        <Grid container>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ m: 1.5, maxHeight: 490, overflow: 'hidden' }}>
              <Grid container>
                <Grid size={12} sx={innerPaper}>
                  <Typography variant="h4">{t('home.step2Title')}</Typography>
                  <Typography variant="body1" sx={{ mt: 1.5 }}>
                    {t('home.step2Desc')}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Box component="img" src="/scoring-squad.png" alt="Squad selection screen" sx={imgStyle} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ m: 1.5, maxHeight: 490, overflow: 'hidden' }}>
              <Grid container>
                <Grid size={12} sx={innerPaper}>
                  <Typography variant="h4">{t('home.step2bTitle')}</Typography>
                  <Typography variant="body1" sx={{ mt: 1.5 }}>
                    {t('home.step2bDesc')}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Box component="img" src="/scoring-squad-shooters.png" alt="Squad shooters list" sx={imgStyle} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      {/* Step 3 - Score (red gradient) */}
      <Grid size={12}>
        <Paper sx={{ mx: 1.5 }}>
          <Grid container sx={{
            background: 'linear-gradient(to bottom, #f44336 0%, #661c17 100%)',
            '& *': { color: 'white' },
          }}>
            <Grid size={{ xs: 12, md: 6 }} sx={innerPaper}>
              <Typography variant="body1">{t('home.step3Pre')}</Typography>
              <Typography variant="h2">{t('home.step3Title')}</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box component="img" src="/scoring-hand.png" alt="Scoring screen" sx={imgStyle} />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Step 4 - Sign + Live scores */}
      <Grid size={12}>
        <Grid container>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ m: 1.5, maxHeight: 490, overflow: 'hidden' }}>
              <Grid container>
                <Grid size={12} sx={innerPaper}>
                  <Typography variant="h4">{t('home.step4Title')}</Typography>
                  <Typography variant="body1" sx={{ mt: 1.5 }}>
                    {t('home.step4Desc')}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Box component="img" src="/scoring-sign.png" alt="Signing form" sx={imgStyle} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ m: 1.5, maxHeight: 490, overflow: 'hidden' }}>
              <Grid container>
                <Grid size={12} sx={innerPaper}>
                  <Typography variant="h4">{t('home.step4bTitle')}</Typography>
                  <Typography variant="body1" sx={{ mt: 1.5 }}>
                    {t('home.step4bDesc')}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Box component="img" src="/scoring-live.png" alt="Live scores" sx={imgStyle} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      {/* Mobile CTA */}
      {isMobile && (
        <Grid size={12} sx={{ pt: 1 }}>
          <CtaButtons isAuthenticated={isAuthenticated} onDemo={onDemo} />
        </Grid>
      )}

      {/* What else? */}
      <Grid size={12} sx={{ px: 1.5 }}>
        <Typography variant="h4" sx={{ my: 3 }}>{t('home.whatElse')}</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', textAlign: 'left', flexDirection: { xs: 'column', md: 'row' }, maxWidth: 800, gap: { xs: 0, md: 4 } }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {featuresLeft.map((f) => (
                <Box key={f} sx={{ display: 'flex', alignItems: 'center', my: 0.5 }}>
                  <CheckIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body1">{f}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {featuresRight.map((f) => (
                <Box key={f} sx={{ display: 'flex', alignItems: 'center', my: 0.5 }}>
                  <CheckIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body1">{f}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Grid>

      {/* Pricing */}
      <Pricing />

      {/* Mobile CTA */}
      {isMobile && (
        <Grid size={12} sx={{ pt: 3 }}>
          <CtaButtons isAuthenticated={isAuthenticated} onDemo={onDemo} />
        </Grid>
      )}

      {/* Contact Us */}
      <ContactUs />

      {/* Footer - spacer */}
      {latestMatches && latestMatches.length > 0 && (
        <Box sx={{ height: 56 }} />
      )}

      {/* Footer - fixed bottom bar */}
      {latestMatches && latestMatches.length > 0 && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          background: 'white',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          zIndex: 10,
          overflowX: 'auto',
        }}>
          {latestMatches.map((m) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const start = new Date(m.startDate.year, m.startDate.month - 1, m.startDate.day);
            const end = m.endDate ? new Date(m.endDate.year, m.endDate.month - 1, m.endDate.day) : start;
            const isToday = start <= new Date(today.getTime() + 86400000 - 1) && end >= today;
            return (
              <Chip
                key={m.alias}
                label={m.title}
                onClick={() => navigate(`/${m.alias}`)}
                color={isToday ? 'primary' : 'default'}
                variant={isToday ? 'filled' : 'outlined'}
                size="small"
                sx={{ flexShrink: 0 }}
              />
            );
          })}
          <Chip
            icon={<PublicIcon />}
            label={t('home.worldMap', 'Map')}
            onClick={() => navigate('/map')}
            variant="outlined"
            size="small"
            sx={{ flexShrink: 0, ml: 'auto' }}
          />
        </Box>
      )}


      {/* Demo Modal */}
      <Modal open={demoOpen}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: '100vh', width: '100vw',
        }}>
          <Box sx={{
            width: 300, minHeight: demoMatchId ? 330 : 170,
            background: 'white', borderRadius: '3px',
            boxShadow: '0px 0px 5px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', p: 3, pb: 4, textAlign: 'center',
          }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {demoMatchId ? t('home.demoReady') : t('home.demoCreating')}
            </Typography>
            {!demoMatchId && <CircularProgress size={36} />}
            {demoMatchId && (
              <>
                <Button
                  fullWidth variant="contained" color="primary" sx={{ mb: 0.75 }}
                  component="a" href={`/${demoMatchId}`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => setDemoOpen(false)}
                >
                  {t('home.demoWebsite')}
                </Button>
                <Typography variant="caption" sx={{ mb: 1.5 }}>{t('home.demoWebsiteDesc')}</Typography>
                <Button
                  fullWidth variant="contained" color="primary" sx={{ mb: 0.75 }}
                  component="a" href={`/${demoMatchId}/manage`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => setDemoOpen(false)}
                >
                  {t('home.demoManagement')}
                </Button>
                <Typography variant="caption" sx={{ mb: 1.5 }}>{t('home.demoManagementDesc')}</Typography>
                <Button
                  fullWidth variant="contained" color="primary" sx={{ mb: 0.75 }}
                  component="a" href={`/${demoMatchId}/manage/scores`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => setDemoOpen(false)}
                >
                  {t('home.demoScoring')}
                </Button>
                <Typography variant="caption">{t('home.demoScoringDesc')}</Typography>
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </Grid>
  );
}
